import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast, ToastContainer } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import { LoginContext } from '../AuthContext/LoginContext';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';

// Full CRUD for "Beep Calls" — missed calls, or calls that only beep once after
// being picked up, etc. CRO logs the number and date/time; a single number may
// beep-call many times (searching a number shows the total logged for it). Each
// row tracks how many times CRO contacted the caller back (+1 button) and CRO
// remarks. Access is restricted to admin / CRO / CRO Lead.
//   GET   /backend/beep-calls/get-all-beep-calls
//   POST  /backend/beep-calls/create-beep-call
//   PATCH /backend/beep-calls/:id
//   PATCH /backend/beep-calls/contacted-back/:id
//   PATCH /backend/beep-calls/delete/:id
const PER_PAGE = 20;

// Convert a Date/ISO string to the value a <input type="datetime-local"> wants.
const toLocalInput = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const formatDateTime = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const BeepCallsCrudLayer = () => {
  const { user } = useContext(LoginContext);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const role = String(user?.role || '').toLowerCase();
  const canManage = role === 'admin' || role === 'cro' || role === 'cro_lead';
  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Add / edit form
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [numberInput, setNumberInput] = useState('');
  const [dateTimeInput, setDateTimeInput] = useState('');
  const [remarksInput, setRemarksInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/backend/beep-calls/get-all-beep-calls`, {
        params: { perPage: PER_PAGE, pageNo: page, searchQuery: appliedSearch || undefined },
        ...authHeader(),
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotalCount(res.data?.totalCount || 0);
      setTotalPages(res.data?.totalPages || 1);
    } catch (err) {
      if (err.response?.status === 404) {
        setRows([]); setTotalCount(0); setTotalPages(1);
      } else {
        console.error('Error fetching beep calls:', err);
        toast.error(err.response?.data?.message || 'Failed to load beep calls.');
      }
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, page, appliedSearch]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const runSearch = (e) => {
    e?.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const clearSearch = () => {
    setSearch('');
    setAppliedSearch('');
    setPage(1);
  };

  const openAdd = () => {
    setEditing(null);
    setNumberInput('');
    setDateTimeInput(toLocalInput(new Date()));
    setRemarksInput('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setNumberInput(row.number || '');
    setDateTimeInput(toLocalInput(row.call_datetime));
    setRemarksInput(row.remarks || '');
    setShowForm(true);
  };

  const saveForm = async () => {
    if (!numberInput.trim()) { toast.warn('Please enter a number.'); return; }
    if (!dateTimeInput) { toast.warn('Please enter the date and time of the call.'); return; }
    setSaving(true);
    try {
      const payload = {
        number: numberInput.trim(),
        call_datetime: new Date(dateTimeInput).toISOString(),
        remarks: remarksInput,
      };
      if (editing) {
        await axios.patch(`${API_BASE_URL}/backend/beep-calls/${editing._id}`, payload, authHeader());
        toast.success('Beep call updated.');
      } else {
        await axios.post(`${API_BASE_URL}/backend/beep-calls/create-beep-call`, payload, authHeader());
        toast.success('Beep call added.');
      }
      setShowForm(false);
      fetchRows();
    } catch (err) {
      console.error('Error saving beep call:', err);
      toast.error(err.response?.data?.message || 'Failed to save beep call.');
    } finally {
      setSaving(false);
    }
  };

  const incrementContact = async (row) => {
    // Optimistic bump
    setRows((prev) => prev.map((r) => (r._id === row._id ? { ...r, contacted_back_count: (r.contacted_back_count || 0) + 1 } : r)));
    try {
      await axios.patch(`${API_BASE_URL}/backend/beep-calls/contacted-back/${row._id}`, {}, authHeader());
    } catch (err) {
      console.error('Error incrementing contacted-back:', err);
      toast.error('Could not update contacted-back count.');
      fetchRows(); // revert to server truth
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await axios.patch(`${API_BASE_URL}/backend/beep-calls/delete/${toDelete._id}`, {}, authHeader());
      toast.success('Beep call deleted.');
      setToDelete(null);
      // If we deleted the last row on a page, step back a page.
      if (rows.length === 1 && page > 1) setPage(page - 1);
      else fetchRows();
    } catch (err) {
      console.error('Error deleting beep call:', err);
      toast.error(err.response?.data?.message || 'Failed to delete beep call.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <ToastContainer position="top-right" autoClose={2500} />

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-wrap align-items-center justify-content-between gap-3">
        <h6 className="text-lg fw-semibold mb-0 d-flex align-items-center gap-2">
          <Icon icon="mdi:phone-alert-outline" className="text-success-main" /> Beep Calls
        </h6>

        <div className="d-flex flex-wrap align-items-center gap-2">
          <form onSubmit={runSearch} className="d-flex align-items-center gap-2">
            <div className="position-relative">
              <input
                type="text"
                className="form-control form-control-sm ps-32"
                style={{ minWidth: 220 }}
                placeholder="Search by number…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Icon icon="ion:search-outline" className="position-absolute top-50 translate-middle-y ms-12 text-secondary-light" style={{ left: 0 }} />
            </div>
            <button type="submit" className="btn btn-sm btn-primary-600">Search</button>
            {appliedSearch && (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearSearch}>Clear</button>
            )}
          </form>
          {canManage && (
            <button className="btn btn-sm btn-success-600 d-flex align-items-center gap-1" onClick={openAdd}>
              <Icon icon="mdi:plus" /> Add Beep Call
            </button>
          )}
        </div>
      </div>

      {/* Search summary: total beep calls for the searched number. */}
      {appliedSearch && !loading && (
        <div className="px-24 py-12 bg-success-50 border-bottom">
          <span className="text-md">
            Total beep calls for <strong>{appliedSearch}</strong>:{' '}
            <span className="badge bg-success-600 text-white px-12 py-4 rounded-pill">{totalCount}</span>
          </span>
        </div>
      )}

      <div className="card-body p-24">
        <div className="table-responsive">
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                {canManage && <th className="text-center">Actions</th>}
                <th>#</th>
                <th>Number</th>
                <th>Date &amp; Time of Call</th>
                <th className="text-center">Contacted Back</th>
                <th>Remarks</th>
                <th>Logged By</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: canManage ? 7 : 6 }).map((__, j) => (
                      <td key={j}><Skeleton height={18} /></td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 7 : 6} className="text-center text-secondary-light py-24">
                    {appliedSearch ? `No beep calls found for “${appliedSearch}”.` : 'No beep calls logged yet.'}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row._id}>
                    {canManage && (
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <button className="btn btn-xs btn-outline-primary-600 px-10 py-4" onClick={() => openEdit(row)} title="Edit / remarks">
                            <Icon icon="mdi:pencil" />
                          </button>
                          <button className="btn btn-xs btn-outline-danger-600 px-10 py-4" onClick={() => setToDelete(row)} title="Delete">
                            <Icon icon="mdi:delete" />
                          </button>
                        </div>
                      </td>
                    )}
                    <td>{(page - 1) * PER_PAGE + idx + 1}</td>
                    <td className="fw-semibold">{row.number}</td>
                    <td>{formatDateTime(row.call_datetime)}</td>
                    <td className="text-center">
                      <span className="badge bg-neutral-200 text-neutral-900 px-10 py-4 rounded-pill me-1">
                        {row.contacted_back_count || 0}
                      </span>
                      {canManage && (
                        <button
                          className="btn btn-xs btn-outline-success-600 px-8 py-2"
                          title="CRO contacted this number back once more"
                          onClick={() => incrementContact(row)}
                        >
                          <Icon icon="mdi:phone-plus" /> +1
                        </button>
                      )}
                    </td>
                    <td style={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>{row.remarks || <span className="text-secondary-light">—</span>}</td>
                    <td>
                      {row.created_by
                        ? `${row.created_by.first_name || ''} ${row.created_by.last_name || ''}`.trim() || '—'
                        : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span className="text-secondary-light text-sm">
              Page {page} of {totalPages} · {totalCount} total
            </span>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <Icon icon="mdi:chevron-left" /> Prev
              </button>
              <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                Next <Icon icon="mdi:chevron-right" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-lg">
            {editing ? 'Edit Beep Call' : 'Add Beep Call'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-16">
            <label className="form-label fw-semibold">Number <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. 03001234567"
              value={numberInput}
              onChange={(e) => setNumberInput(e.target.value)}
            />
          </div>
          <div className="mb-16">
            <label className="form-label fw-semibold">Date &amp; Time of Call <span className="text-danger">*</span></label>
            <input
              type="datetime-local"
              className="form-control"
              value={dateTimeInput}
              onChange={(e) => setDateTimeInput(e.target.value)}
            />
          </div>
          <div className="mb-4">
            <label className="form-label fw-semibold">Remarks (by CRO)</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Optional remarks…"
              value={remarksInput}
              onChange={(e) => setRemarksInput(e.target.value)}
            />
          </div>
          {editing && (
            <p className="text-secondary-light text-sm mb-0">
              Contacted-back count: <strong>{editing.contacted_back_count || 0}</strong> — use the “+1” button in the table to increase it.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={saving}>Cancel</Button>
          <Button variant="success" onClick={saveForm} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete confirm modal */}
      <Modal show={!!toDelete} onHide={() => setToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-lg">Delete Beep Call</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the beep call from{' '}
          <strong>{toDelete?.number}</strong> ({formatDateTime(toDelete?.call_datetime)})?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setToDelete(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BeepCallsCrudLayer;
